import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";

interface ImageCropperProps {
  src: string;
  aspect?: number; // default 1 (square)
  size?: number; // output size in px, default 512
  onCancel: () => void;
  onConfirm: (blob: Blob) => void;
}

export function ImageCropper({ src, aspect = 1, size = 512, onCancel, onConfirm }: ImageCropperProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);
  const [scale, setScale] = useState(1); // user scale multiplier
  const [tx, setTx] = useState(0); // translateX in container px
  const [ty, setTy] = useState(0); // translateY in container px
  const [dragging, setDragging] = useState(false);
  const lastPos = useRef<{ x: number; y: number } | null>(null);
  const [containerSize, setContainerSize] = useState(320); // responsive square in CSS px

  // Measure available width and pick a square that fits the dialog on mobile
  useLayoutEffect(() => {
    const measure = () => {
      const parent = containerRef.current?.parentElement;
      const avail = parent ? parent.clientWidth : 320;
      const next = Math.max(220, Math.min(380, Math.floor(avail)));
      setContainerSize(next);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Compute base scale so image covers the square container
  const baseScale = useMemo(() => {
    if (!naturalSize) return 1;
    const { w, h } = naturalSize;
    return Math.max(containerSize / w, containerSize / h);
  }, [naturalSize, containerSize]);


  const displayedSize = useMemo(() => {
    if (!naturalSize) return { w: 0, h: 0 };
    const w = naturalSize.w * baseScale * scale;
    const h = naturalSize.h * baseScale * scale;
    return { w, h };
  }, [naturalSize, baseScale, scale]);

  const maxOffsets = useMemo(() => {
    // Prevent empty areas: limit translation so image always covers container
    const maxX = Math.max(0, (displayedSize.w - containerSize) / 2);
    const maxY = Math.max(0, (displayedSize.h - containerSize) / 2);
    return { maxX, maxY };
  }, [displayedSize]);

  useEffect(() => {
    // Clamp translation when scale changes
    setTx((prev) => Math.max(-maxOffsets.maxX, Math.min(maxOffsets.maxX, prev)));
    setTy((prev) => Math.max(-maxOffsets.maxY, Math.min(maxOffsets.maxY, prev)));
  }, [maxOffsets.maxX, maxOffsets.maxY]);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
  };

  const onPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setDragging(true);
    lastPos.current = { x: e.clientX, y: e.clientY };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging || !lastPos.current) return;
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    lastPos.current = { x: e.clientX, y: e.clientY };

    setTx((prev) => {
      const next = prev + dx;
      return Math.max(-maxOffsets.maxX, Math.min(maxOffsets.maxX, next));
    });
    setTy((prev) => {
      const next = prev + dy;
      return Math.max(-maxOffsets.maxY, Math.min(maxOffsets.maxY, next));
    });
  };

  const onPointerUp = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    setDragging(false);
    lastPos.current = null;
  };

  const handleConfirm = async () => {
    if (!imgRef.current || !naturalSize) return;

    const { w: natW, h: natH } = naturalSize;

    // Compute original-space crop rect corresponding to container square
    const effScale = baseScale * scale; // container px per original px
    const cropW = containerSize / effScale;
    const cropH = (containerSize / effScale) / aspect * aspect; // square by default

    const centerX = natW / 2 - tx / effScale;
    const centerY = natH / 2 - ty / effScale;

    let sx = Math.round(centerX - cropW / 2);
    let sy = Math.round(centerY - cropH / 2);
    let sWidth = Math.round(cropW);
    let sHeight = Math.round(cropH);

    // Clamp within image bounds
    sx = Math.max(0, Math.min(natW - sWidth, sx));
    sy = Math.max(0, Math.min(natH - sHeight, sy));

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = Math.round(size / aspect);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(imgRef.current, sx, sy, sWidth, sHeight, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (blob) onConfirm(blob);
    }, 'image/jpeg', 0.95);
  };

  return (
    <div className="space-y-4">
      <div
        ref={containerRef}
        className="relative mx-auto border border-border rounded-md overflow-hidden touch-none select-none"
        style={{ width: containerSize, height: containerSize, touchAction: 'none' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >

        {/* Cropping mask (square) */}
        <div className="absolute inset-0 pointer-events-none bg-black/10" />
        {/* Image centered with base size, then transformed by user scale and translation */}
        <img
          ref={imgRef}
          src={src}
          onLoad={onImageLoad}
          alt="Crop"
          draggable={false}
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: naturalSize ? naturalSize.w * baseScale : 'auto',
            height: naturalSize ? naturalSize.h * baseScale : 'auto',
            transform: `translate(-50%, -50%) translate(${tx}px, ${ty}px) scale(${scale})`,
            transformOrigin: 'center center',
            userSelect: 'none',
            cursor: dragging ? 'grabbing' : 'grab',
          }}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Zoom</span>
          <span>{Math.round(scale * 100)}%</span>
        </div>
        <Slider
          min={1}
          max={3}
          step={0.01}
          value={[scale]}
          onValueChange={(v) => setScale(v[0] ?? 1)}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleConfirm}>Apply Crop</Button>
      </div>
    </div>
  );
}
